import { useState } from "react";
import { Eye, RefreshCw } from "lucide-react";

import { DataTable } from "components/ui/DataTable/DataTable";
import { Pill } from "components/ui/Pill/Pill";
import { useReferralsPaginatedList } from "features/referrals/hooks/useReferralsPaginatedList";
import { useResendReferral } from "features/referrals/hooks/useResendReferral";
import { formatDate } from "utils/dateFormatter";

const ROWS_OPTIONS = [10, 20, 50, 100];

const SORT_OPTIONS = [
  { field: "newest-first", direction: "", label: "Newest first" },
  { field: "oldest-first", direction: "", label: "Oldest first" },
];

const STATUS_CONFIG = {
  SENT: {
    color: "var(--green-900)",
    bg: "#e7efeb",
    border: "var(--green-900)",
    label: "Sent",
  },
  ACCEPTED: {
    color: "var(--green-900)",
    bg: "#e7efeb",
    border: "var(--green-900)",
    label: "Accepted",
  },
  REJECTED: {
    color: "#b70000",
    bg: "#ffe8e8",
    border: "#b70000",
    label: "Rejected",
  },
  DRAFT: {
    color: "#756800",
    bg: "#efefe7",
    border: "#756800",
    label: "Draft",
  },
  ERROR: {
    color: "#b70000",
    bg: "#ffe8e8",
    border: "#b70000",
    label: "Error",
  },
};

const normalizeStatus = (status) =>
  ["INTEGRATED_BROWSER", "DRAFT"].includes(status) ? "DRAFT" : status;

const renderStatusPill = (row) => {
  const key = normalizeStatus(row.status);
  const cfg = STATUS_CONFIG[key] ?? {
    color: "var(--gray-600)",
    bg: "var(--gray-100)",
    border: "var(--gray-300)",
    label: key || "-",
  };
  return (
    <Pill color={cfg.color} bg={cfg.bg} border={cfg.border}>
      {cfg.label}
    </Pill>
  );
};

const COLUMNS = [
  {
    id: "date",
    header: "Date",
    width: "180px",
    render: (row) => formatDate(row.date) ?? "-",
  },
  {
    id: "hospital",
    header: "Hospital",
    flexGrow: 1,
    render: (row) => row.hospital ?? "-",
  },
  {
    id: "referralType",
    header: "Referral type",
    flexGrow: 1,
    render: (row) => row.referralName || row.referralType || "-",
  },
  {
    id: "status",
    header: "Status",
    width: "140px",
    render: renderStatusPill,
  },
];

export const ResultsTable = ({ setSelectedReferral, patientId }) => {
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [sort, setSort] = useState({ field: "newest-first", direction: "" });

  const { data, isLoading } = useReferralsPaginatedList({
    page,
    patientId,
    sorting: sort.field,
    rows: rowsPerPage,
  });

  const { mutate: resendReferral } = useResendReferral();

  const rowActions = [
    {
      label: "Resend",
      icon: RefreshCw,
      primary: true,
      visibleWhen: (row) => row.status === "ERROR",
      onClick: (row) => resendReferral({ referralId: row.id }),
    },
    {
      label: "Open",
      icon: Eye,
      primary: true,
      onClick: (row) => setSelectedReferral(row),
    },
  ];

  return (
    <DataTable
      columns={COLUMNS}
      data={data?.data ?? []}
      getRowId={(row) => row.id}
      isLoading={isLoading}
      emptyTitle="No referrals found"
      emptyText="There are no referrals to display at this time."
      sort={sort}
      sortOptions={SORT_OPTIONS}
      onSortChange={(field, direction) => {
        setSort({ field, direction });
        setPage(1);
      }}
      rowActions={rowActions}
      actionsColumnWidth="80px"
      currentPage={page}
      rowsPerPage={rowsPerPage}
      rowsValue={rowsPerPage}
      rowsOptions={ROWS_OPTIONS}
      totalCount={data?.totalRecords}
      onPageChange={setPage}
      onRowsChange={(val) => {
        setRowsPerPage(val);
        setPage(1);
      }}
    />
  );
};
